import React, { useState } from "react";
import { motion } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

/**
 * ExitSurveyModal — captures why the athlete is cancelling BEFORE we
 * call the cancel endpoint. The survey feeds back into Hormozi's pattern
 * of reviewing top-3 churn reasons from every 20 cancels.
 *
 * Flow: athlete selects 1+ reasons and/or writes free text -> Submit &
 * Cancel -> we POST the survey, then call the cancel endpoint. If the
 * athlete clicks "Never mind", we do nothing — sometimes the act of
 * articulating changes their mind.
 */
const REASONS = [
  { key: "price", label: "Too expensive" },
  { key: "engagement", label: "Not using it enough" },
  { key: "value", label: "Not getting the results I wanted" },
  { key: "schedule", label: "Doesn't fit my schedule right now" },
  { key: "competitor", label: "Switching to a different solution" },
  { key: "other", label: "Something else" },
];

export const ExitSurveyModal = ({ open, onClose, onConfirmCancel, cancelling }) => {
  const [reasons, setReasons] = useState([]);
  const [freeText, setFreeText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const toggleReason = (key) => {
    setReasons((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const canSubmit = reasons.length > 0 || freeText.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit || submitting || cancelling) return;
    setSubmitting(true);
    try {
      // Record the survey FIRST so we keep the data even if cancellation fails
      const r = await fetch(`${API}/feedback/exit-survey`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reasons, free_text: freeText }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || "Couldn't save your feedback");
        setSubmitting(false);
        return;
      }
    } catch {
      toast.error("Network error saving feedback");
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    // Hand control back to the parent to process the cancellation
    onConfirmCancel();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={() => !submitting && !cancelling && onClose()}
      data-testid="exit-survey-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-full max-w-lg bg-surface border border-border rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-red-400 font-bold">
              Before you go
            </p>
            <h3 className="text-xl sm:text-2xl font-bold text-white mt-1">
              What made you want to cancel?
            </h3>
            <p className="text-sm text-text-secondary mt-2">
              Takes 30 seconds. It genuinely helps us build something better.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting || cancelling}
            className="p-2 hover:bg-white/5 rounded-lg disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-text-muted" />
          </button>
        </div>

        <div className="space-y-2 mb-4">
          {REASONS.map((r) => {
            const selected = reasons.includes(r.key);
            return (
              <button
                key={r.key}
                onClick={() => toggleReason(r.key)}
                disabled={submitting || cancelling}
                className={`w-full text-left px-4 py-3 rounded-lg border transition-all flex items-center gap-3 ${
                  selected
                    ? "bg-red-500/10 border-red-500/40 text-white"
                    : "bg-surface border-border text-text-secondary hover:border-red-500/20"
                }`}
                data-testid={`exit-reason-${r.key}`}
              >
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    selected ? "border-red-400 bg-red-400/20" : "border-border"
                  }`}
                >
                  {selected && <div className="w-2 h-2 rounded-full bg-red-400" />}
                </div>
                <span className="text-sm">{r.label}</span>
              </button>
            );
          })}
        </div>

        <div className="mb-5">
          <label className="text-xs text-text-muted uppercase tracking-wide mb-2 block">
            Anything else? (optional)
          </label>
          <textarea
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            disabled={submitting || cancelling}
            rows={3}
            maxLength={1000}
            placeholder="The one thing we could have done better…"
            className="w-full px-4 py-3 bg-[#0B0F1A] border border-border rounded-xl text-white placeholder-text-muted focus:border-red-500/40 outline-none resize-none text-sm"
            data-testid="exit-free-text"
          />
          <p className="text-[10px] text-text-muted mt-1 text-right">{freeText.length}/1000</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={onClose}
            disabled={submitting || cancelling}
            className="flex-1 py-3 bg-surface border border-border rounded-lg hover:border-primary/50 transition-colors text-sm font-medium text-white disabled:opacity-50"
            data-testid="exit-never-mind"
          >
            Never mind — keep my subscription
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting || cancelling}
            className="flex-1 py-3 bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/30 transition-colors disabled:opacity-40 text-sm font-medium flex items-center justify-center gap-2"
            data-testid="exit-submit-cancel"
          >
            {submitting ? "Saving…" : cancelling ? "Cancelling…" : (
              <>
                Submit & Cancel
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ExitSurveyModal;
